import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import { FiX, FiArrowLeft, FiArrowRight } from 'react-icons/fi';

function getPhotoUrl(entry) {
  return entry.photos?.find(p => p.useInTimelapse)?.url
    || entry.photos?.[0]?.url
    || entry.imageUrl;
}

function CompareSlider({ leftEntry, rightEntry }) {
  const [divider, setDivider] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const updatePos = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDivider(Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100)));
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full select-none overflow-hidden rounded-3xl shadow-lg bg-black"
      style={{ height: 340 }}
      onMouseMove={e => dragging.current && updatePos(e.clientX)}
      onMouseUp={() => { dragging.current = false; }}
      onMouseLeave={() => { dragging.current = false; }}
      onTouchMove={e => { e.preventDefault(); updatePos(e.touches[0].clientX); }}
    >
      {/* Right photo (behind) */}
      <img src={getPhotoUrl(rightEntry)} alt="right"
        className="absolute inset-0 w-full h-full object-cover object-top" />

      {/* Left photo (clipped to left portion) */}
      <div className="absolute inset-0"
        style={{ clipPath: `inset(0 ${(100 - divider).toFixed(1)}% 0 0)` }}>
        <img src={getPhotoUrl(leftEntry)} alt="left"
          className="w-full h-full object-cover object-top" />
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-px pointer-events-none"
        style={{ left: `${divider}%`, background: 'rgba(255,255,255,0.9)', boxShadow: '0 0 8px rgba(255,255,255,0.6)' }}
      />

      {/* Drag handle */}
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-white shadow-xl flex items-center justify-center cursor-col-resize z-10 touch-none"
        style={{ left: `${divider}%` }}
        onMouseDown={() => { dragging.current = true; }}
        onTouchStart={e => { e.stopPropagation(); }}
      >
        <SafeIcon icon={FiArrowLeft} className="text-stone-600 text-xs" />
        <SafeIcon icon={FiArrowRight} className="text-stone-600 text-xs" />
      </div>

      {/* Date labels */}
      <div className="absolute bottom-4 left-3 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full pointer-events-none">
        <p className="text-white font-sans text-xs font-medium">{format(parseISO(leftEntry.date), 'MMM d, yyyy')}</p>
      </div>
      <div className="absolute bottom-4 right-3 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full pointer-events-none">
        <p className="text-white font-sans text-xs font-medium">{format(parseISO(rightEntry.date), 'MMM d, yyyy')}</p>
      </div>
    </div>
  );
}

export default function PhotoCompare({ entries, onClose }) {
  const photoEntries = entries
    .filter(e => getPhotoUrl(e))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const [step, setStep] = useState('pick');
  const [selected, setSelected] = useState([]);

  const toggleSelect = (entry) => {
    if (selected.find(e => e.id === entry.id)) {
      setSelected(s => s.filter(e => e.id !== entry.id));
    } else if (selected.length < 2) {
      setSelected(s => [...s, entry]);
    }
  };

  const canCompare = selected.length === 2;
  const [left, right] = selected.sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      className="absolute inset-0 z-50 bg-[#f8fafc] flex flex-col"
    >
      {/* Header */}
      <div className="px-5 pt-8 pb-4 flex-shrink-0 flex items-center justify-between">
        <div>
          <h2 className="font-sans text-xl font-semibold text-slate-900">
            {step === 'pick' ? 'Pick 2 Photos' : 'Comparison'}
          </h2>
          {step === 'pick' && (
            <p className="font-sans text-xs text-slate-400 mt-0.5">{selected.length}/2 selected · drag the divider to compare</p>
          )}
        </div>
        <button onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/80 border border-slate-200 flex items-center justify-center shadow-sm">
          <SafeIcon icon={FiX} className="text-slate-600 text-lg" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-10">
        {step === 'pick' ? (
          photoEntries.length < 2 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-5xl mb-4">📸</p>
              <p className="font-sans text-lg font-medium text-slate-700 mb-2">Not enough photos</p>
              <p className="font-sans text-sm text-slate-400">Log at least 2 entries with photos to compare.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {photoEntries.map(entry => {
                  const isSel = !!selected.find(e => e.id === entry.id);
                  const selIdx = selected.findIndex(e => e.id === entry.id);
                  const isDisabled = !isSel && selected.length === 2;
                  return (
                    <button key={entry.id} onClick={() => toggleSelect(entry)} disabled={isDisabled}
                      className={`relative aspect-square rounded-2xl overflow-hidden transition-all ${
                        isSel ? 'ring-2 ring-offset-2 ring-slate-900' : isDisabled ? 'opacity-35' : 'active:scale-95'
                      }`}>
                      <img src={getPhotoUrl(entry)} alt=""
                        className="w-full h-full object-cover object-top" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                      <div className="absolute bottom-2 left-0 right-0 flex flex-col items-center">
                        <p className="text-white font-sans text-xs font-medium">
                          {format(parseISO(entry.date), 'MMM d')}
                        </p>
                        <p className="text-white/70 font-sans text-xs">✦ {entry.glowScore}/10</p>
                      </div>
                      {isSel && (
                        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-slate-900 text-white font-sans text-xs font-bold flex items-center justify-center shadow">
                          {selIdx + 1}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <motion.button whileTap={{ scale: 0.97 }}
                onClick={() => canCompare && setStep('compare')}
                disabled={!canCompare}
                className={`w-full py-4 rounded-[2rem] font-sans font-semibold text-white text-base transition-all ${
                  canCompare ? 'bg-slate-900 shadow-lg' : 'bg-slate-300'
                }`}>
                Compare Photos
              </motion.button>
            </>
          )
        ) : (
          <>
            <CompareSlider leftEntry={left} rightEntry={right} />

            {/* Score comparison */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              {[left, right].map(entry => (
                <div key={entry.id} className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm text-center">
                  <p className="font-serif text-3xl text-stone-900">{entry.glowScore}</p>
                  <p className="font-sans text-xs text-stone-400 mt-0.5">/10 glow</p>
                  <p className="font-sans text-xs font-semibold text-stone-600 mt-2">
                    {format(parseISO(entry.date), 'MMM d, yyyy')}
                  </p>
                  {entry.tags?.length > 0 && (
                    <p className="font-sans text-xs text-stone-400 mt-1 truncate">{entry.tags.slice(0, 2).join(', ')}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Delta callout */}
            {(() => {
              const delta = right.glowScore - left.glowScore;
              if (delta === 0) return (
                <div className="mt-3 rounded-2xl px-4 py-3 text-center bg-stone-50 border border-stone-100">
                  <p className="font-sans text-sm font-semibold text-stone-500">→ Glow score unchanged</p>
                </div>
              );
              return (
                <div className={`mt-3 rounded-2xl px-4 py-3 text-center ${delta > 0 ? 'bg-emerald-50 border border-emerald-100' : 'bg-rose-50 border border-rose-100'}`}>
                  <p className={`font-sans text-sm font-semibold ${delta > 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                    {delta > 0 ? `↑ Glow improved by ${delta} point${Math.abs(delta) !== 1 ? 's' : ''}` : `↓ Glow dropped by ${Math.abs(delta)} point${Math.abs(delta) !== 1 ? 's' : ''}`}
                  </p>
                  <p className="font-sans text-xs text-stone-400 mt-1">between these two entries</p>
                </div>
              );
            })()}

            <button onClick={() => setStep('pick')}
              className="mt-4 w-full py-3 rounded-2xl font-sans text-sm text-stone-500 bg-white border border-stone-200 active:bg-stone-50">
              ← Pick different photos
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
}
